/**
 * Saved poster designs.
 *
 * Presets hold only the *design* — colours, pattern, ornaments, fonts, copy —
 * never the album identity, so one preset can be applied to any album.
 *
 * Stored in localStorage: this is a per-person styling preference, and keeping
 * it client-side avoids an API round trip on a screen that is otherwise
 * entirely local. The trade-off is that presets do not follow a user between
 * devices.
 */

import {
  PosterBackground,
  PosterFontChoices,
  PosterMascotPosition,
  PosterMascotSize,
  PosterMascots,
  PosterOrnaments,
  PatternSettings,
  POSTER_BACKGROUNDS,
  POSTER_FONTS,
  POSTER_MASCOTS,
  POSTER_MASCOT_POSITIONS,
  POSTER_ORNAMENTS,
  MASCOT_SIZES,
  defaultPatternSettings,
} from './posterDesign';

const STORAGE_KEY = 'pixelmachha.posterPresets.v1';
const MAX_PRESETS = 20;

export interface PosterDesignConfig {
  message: string;
  showUrl: boolean;
  fonts: PosterFontChoices;
  bgPrimary: string;
  bgSecondary: string;
  background: PosterBackground;
  patternSettings: PatternSettings;
  ornaments: PosterOrnaments;
  ornamentCount: number;
  ornamentSeed: number;
  mascots: PosterMascots;
  mascotPosition: PosterMascotPosition;
  mascotSize: PosterMascotSize;
}

export interface PosterPreset {
  id: string;
  name: string;
  /** Epoch ms, used to sort newest-first. */
  savedAt: number;
  config: PosterDesignConfig;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function num(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, n));
}

function hex(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX.test(value.trim()) ? value.trim() : fallback;
}

/**
 * Coerce anything read from storage into a usable config.
 *
 * Presets outlive the code that wrote them: a stored value may predate an
 * option being renamed or removed, and localStorage is user-editable. Every
 * field is validated against the current option lists so a stale or hand-edited
 * entry degrades to defaults instead of rendering a broken poster.
 */
export function sanitizeConfig(
  raw: unknown,
  defaults: PosterDesignConfig,
): PosterDesignConfig {
  const r = (raw ?? {}) as Partial<PosterDesignConfig>;

  const background = oneOf(
    r.background,
    POSTER_BACKGROUNDS.map((b) => b.key),
    defaults.background,
  );
  const patternDefaults = defaultPatternSettings(background);
  const ps = (r.patternSettings ?? {}) as Partial<PatternSettings>;

  const fontKeys = POSTER_FONTS.map((f) => f.key);
  const rf = (r.fonts ?? {}) as Partial<PosterFontChoices>;

  return {
    message:
      typeof r.message === 'string' && r.message.trim()
        ? r.message.slice(0, 90)
        : defaults.message,
    showUrl: typeof r.showUrl === 'boolean' ? r.showUrl : defaults.showUrl,
    fonts: {
      eventName: oneOf(rf.eventName, fontKeys, defaults.fonts.eventName),
      message: oneOf(rf.message, fontKeys, defaults.fonts.message),
      albumName: oneOf(rf.albumName, fontKeys, defaults.fonts.albumName),
    },
    bgPrimary: hex(r.bgPrimary, defaults.bgPrimary),
    bgSecondary: hex(r.bgSecondary, defaults.bgSecondary),
    background,
    patternSettings: {
      scale: num(ps.scale, patternDefaults.scale, 0.1, 5),
      thickness: num(ps.thickness, patternDefaults.thickness, 0.1, 8),
      density: num(ps.density, patternDefaults.density, 0.1, 5),
      opacity: num(ps.opacity, patternDefaults.opacity, 0, 3),
      angle: num(ps.angle, patternDefaults.angle, 0, 360),
    },
    ornaments: oneOf(r.ornaments, POSTER_ORNAMENTS.map((o) => o.key), defaults.ornaments),
    ornamentCount: Math.round(num(r.ornamentCount, defaults.ornamentCount, 0, 60)),
    ornamentSeed: Math.round(num(r.ornamentSeed, defaults.ornamentSeed, 1, 1e9)),
    mascots: oneOf(r.mascots, POSTER_MASCOTS.map((m) => m.key), defaults.mascots),
    mascotPosition: oneOf(
      r.mascotPosition,
      POSTER_MASCOT_POSITIONS.map((m) => m.key),
      defaults.mascotPosition,
    ),
    mascotSize: oneOf(r.mascotSize, MASCOT_SIZES.map((m) => m.key), defaults.mascotSize),
  };
}

function read(): PosterPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is PosterPreset =>
        !!p && typeof p.id === 'string' && typeof p.name === 'string' && !!p.config,
    );
  } catch {
    // Corrupt or unavailable storage (private mode, quota) — start empty
    // rather than breaking the designer.
    return [];
  }
}

function write(presets: PosterPreset[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    return true;
  } catch {
    return false;
  }
}

/** Saved presets, newest first. */
export function listPresets(): PosterPreset[] {
  return read().sort((a, b) => b.savedAt - a.savedAt);
}

/**
 * Save under `name`, replacing any existing preset with the same name so
 * re-saving a tweaked design updates it rather than piling up duplicates.
 * Returns null when storage is unavailable.
 */
export function savePreset(name: string, config: PosterDesignConfig): PosterPreset | null {
  const trimmed = name.trim().slice(0, 60);
  if (!trimmed) return null;

  const existing = read().filter((p) => p.name.toLowerCase() !== trimmed.toLowerCase());
  const preset: PosterPreset = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: trimmed,
    savedAt: Date.now(),
    config,
  };

  const next = [preset, ...existing].slice(0, MAX_PRESETS);
  return write(next) ? preset : null;
}

export function deletePreset(id: string): void {
  write(read().filter((p) => p.id !== id));
}
